import React, { useEffect, useState } from "react";
import { Modal, Image } from "antd";
import { FaFileMedicalAlt, FaTimes } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import drEasyApi from "../../../Api/Customapi";
import { getImg } from "../../../Api/getImg";

export default function ReportModal({ visible, onCancel, bookingId }) {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!visible) return;

    const fetchReport = async () => {
      setLoading(true);
      try {
        const res = await drEasyApi.get("/api/Reports/GetMyReport");
        setReportData(res.data);
      } catch (err) {
        console.error("Failed to fetch report", err);
        setReportData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [visible]);

  const matchingRow = reportData?.rows.find((r) => r.bookingId === bookingId);

  const getTextFor = (columnId) => {
    const cell = matchingRow?.cells?.find((c) => c.columnId === columnId);
    return cell?.text || "";
  };

  const getPhotoFor = (columnId) => {
    const cell = matchingRow?.cells?.find((c) => c.columnId === columnId);
    return cell?.photoId || null;
  };

  return (
    <Modal
      open={visible}
      onCancel={onCancel}
      footer={null}
      centered
      closeIcon={<FaTimes className="text-brand text-xl" />}
      bodyStyle={{ padding: 0, overflow: "hidden" }}
      width={800}
      destroyOnClose
    >
      <AnimatePresence>
        {visible && (
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-lg font-sans p-6"
          >
            {/* Header */}
            <div className="border-b border-gray-300 pb-4 mb-4 flex items-center gap-3">
              <FaFileMedicalAlt className="text-brand text-3xl text-textPrimary" />
              <h2 className="text-2xl font-bold text-textPrimary">Medical Report</h2>
            </div>

            {/* Body */}
            {loading ? (
              <div className="flex justify-center py-20">
                <div className="animate-spin border-y-2 border-primary rounded-full w-12 h-12" />
              </div>
            ) : !matchingRow ? (
              <p className="text-center italic text-gray-500">
                No notes found for this booking.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="table-auto min-w-full border-collapse border">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="border px-2 py-1 text-left w-40">Date</th>
                      {reportData.columns.map((col) => (
                        <th key={col.id} className="border px-2 py-1 text-left w-60">
                          {col.title}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border px-2 py-1 text-sm font-medium text-gray-700">
                        {new Date(matchingRow.bookingDate).toLocaleDateString()}
                      </td>
                      {reportData.columns.map((col) => {
                        const text = getTextFor(col.id);
                        const photoId = getPhotoFor(col.id);
                        return (
                          <td key={col.id} className="border px-2 py-1 text-sm">
                            <div className="flex flex-col gap-2">
                              {text && <span>{text}</span>}
                              {photoId && (
                                <Image
                                  width={60}
                                  height={60}
                                  preview
                                  src={getImg(photoId)}
                                  alt="Preview"
                                  style={{
                                    borderRadius: "4px",
                                    objectFit: "cover",
                                    border: "1px solid #ccc",
                                  }}
                                />
                              )}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </Modal>
  );
}
